import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  BellRing,
  CalendarClock,
  ChevronDown,
  ChevronUp,
  Info,
  X,
} from "lucide-react";
import { walkSchedulesAPI } from "../../../services/walkSchedulesAPI";

const severityClass = {
  danger: "border-red-200 bg-red-50 text-red-800",
  warning: "border-amber-200 bg-amber-50 text-amber-800",
  info: "border-blue-200 bg-blue-50 text-blue-800",
};

const SeverityIcon = ({ severity }) => {
  if (severity === "danger") return <AlertTriangle className="h-4 w-4" />;
  if (severity === "warning") return <CalendarClock className="h-4 w-4" />;
  return <Info className="h-4 w-4" />;
};

export default function ProjectAttentionCenter({ projectId }) {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [hidden, setHidden] = useState(false);

  const load = useCallback(async () => {
    if (!projectId || document.visibilityState === "hidden") return;
    try {
      const response = await walkSchedulesAPI.getAttentionItems(projectId, 5);
      setItems(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to load project attention items", error);
    }
  }, [projectId]);

  useEffect(() => {
    setHidden(false);
    load();
    const timer = window.setInterval(load, 90000);
    const onVisibility = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [load]);

  const visibleItems = useMemo(
    () => (expanded ? items.slice(0, 3) : items.slice(0, 1)),
    [expanded, items],
  );

  const openItem = async (item) => {
    try {
      await walkSchedulesAPI.markNotificationRead(item.id);
    } catch (error) {
      console.error("Failed to mark notification read", error);
    }
    setItems((current) => current.filter((entry) => entry.id !== item.id));

    const payload = item.payload || {};
    if (item.action === "open_walk_schedule") {
      const detail = {
        tab: "walk-scheduler",
        action: "openWalkSchedule",
        walkScheduleId: payload.walk_schedule_id,
      };
      window.dispatchEvent(
        new CustomEvent("smokyq:navigate-project-tab", { detail }),
      );
      window.setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("smokyq:walk-schedule-open", { detail }),
        );
      }, 120);
      return;
    }

    if (item.action === "open_finding") {
      window.dispatchEvent(
        new CustomEvent("smokyq:navigate-project-tab", {
          detail: {
            tab: "issues-control",
            action: "openFinding",
            findingId: payload.finding_id,
            inspectionId: payload.inspection_id,
          },
        }),
      );
      return;
    }

    if (item.action === "open_inspection") {
      window.dispatchEvent(
        new CustomEvent("smokyq:navigate-project-tab", {
          detail: {
            tab: "inspections",
            action: "openInspection",
            inspectionId: payload.inspection_id,
          },
        }),
      );
    }
  };

  if (!projectId || hidden || items.length === 0) return null;

  return (
    <aside className="fixed bottom-24 right-4 z-40 w-[380px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
      <div className="flex items-center justify-between border-b border-gray-100 bg-gray-950 px-4 py-3 text-white">
        <div className="flex items-center gap-2">
          <BellRing className="h-4 w-4 text-orange-400" />
          <div>
            <div className="text-sm font-semibold">Project attention</div>
            <div className="text-[11px] text-gray-300">
              {items.length} unread update{items.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {items.length > 1 && (
            <button
              type="button"
              onClick={() => setExpanded((value) => !value)}
              className="rounded-lg p-1.5 hover:bg-white/10"
              aria-label={expanded ? "Collapse alerts" : "Expand alerts"}
            >
              {expanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronUp className="h-4 w-4" />
              )}
            </button>
          )}
          <button
            type="button"
            onClick={() => setHidden(true)}
            className="rounded-lg p-1.5 hover:bg-white/10"
            aria-label="Hide project attention"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="max-h-80 divide-y divide-gray-100 overflow-y-auto">
        {visibleItems.map((item) => (
          <div key={item.id} className="p-3">
            <div
              className={`rounded-xl border p-3 ${
                severityClass[item.severity] || severityClass.info
              }`}
            >
              <div className="flex items-start gap-2">
                <span className="mt-0.5 shrink-0">
                  <SeverityIcon severity={item.severity} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold uppercase tracking-wide opacity-80">
                    {item.title}
                  </div>
                  <p className="mt-1 text-sm leading-5">{item.message}</p>
                  {item.action_label && (
                    <button
                      type="button"
                      onClick={() => openItem(item)}
                      className="mt-2 rounded-lg bg-white/80 px-3 py-1.5 text-xs font-semibold shadow-sm ring-1 ring-black/5 hover:bg-white"
                    >
                      {item.action_label}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
}
